<template>
  <div id="PropertyImages" class="p-4">

    <vx-card
      class="mb-6 processing-card"
      :class="{'card-disabled': !property.Id || property.Id <= 0}"
    >
      <div class="p-2">
        <h4 class="mb-4 flex items-center">
          <feather-icon icon="UploadCloudIcon" class="mr-2 text-primary" />
          {{ $t('UploadNewImages') }}
        </h4>

        <div
          class="upload-zone flex flex-col items-center justify-center p-10 border-2 border-dashed rounded-lg transition-all"
          :class="{
            'border-primary bg-primary-light': isDragging,
            'cursor-pointer': property.Id > 0,
            'cursor-not-allowed': !property.Id || property.Id <= 0
          }"
          @dragover.prevent="property.Id > 0 ? isDragging = true : null"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="property.Id > 0 ? onFileDrop($event) : null"
          @click="property.Id > 0 ? $refs.fileInput.click() : null"
        >
          <feather-icon icon="ImageIcon" svgClasses="h-12 w-12 text-grey mb-4" />
          <p class="text-xl font-medium text-grey mb-2">{{ $t('DragDropImages') }}</p>
          <p class="text-sm text-grey">{{ $t('OrClickToBrowse') }}</p>
          <small class="text-muted mt-2">PNG, JPG, WEBP (Max 5MB each)</small>

          <p v-if="!property.Id || property.Id <= 0" class="text-danger mt-4 font-bold">
            {{ $t('PleaseSavePropertyFirst') }}
          </p>
        </div>

        <input
          type="file"
          ref="fileInput"
          multiple
          accept="image/png, image/jpeg, image/webp"
          class="hidden"
          @change="onFileSelect"
          :disabled="!property.Id || property.Id <= 0"
        />
      </div>
    </vx-card>

    <vx-card v-if="propertyImages.length > 0">
      <div class="p-2">
        <div class="flex items-center justify-between mb-6">
          <h4 class="flex items-center">
            <feather-icon icon="GridIcon" class="mr-2 text-success" />
            {{ $t('PropertyGallery') }}
            <vs-chip class="ml-3" color="success">{{ propertyImages.length }} {{ $t('Images') }}</vs-chip>
          </h4>
          <vs-button color="primary" icon-pack="feather" icon="icon-save" @click="submitImages" :disabled="isProcessing">
            {{ $t('SaveChanges') }}
          </vs-button>
        </div>

        <div class="vx-row property-grid mt-4">
          <div v-for="(image, index) in propertyImages" :key="image.Id" class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/4 mb-8" >
            <div class="img-card rounded-lg shadow-md overflow-hidden bg-white border border-solid border-grey-light flex flex-col h-full">

              <div class="relative aspect-square bg-black overflow-hidden">
                <img :src="baseURL + image.ImagePath" class="w-full h-full object-cover" />
                <vs-chip v-if="image.IsPrimary" color="warning" class="absolute top-2 left-2 shadow-lg">
                  {{ $t('Primary') }}
                </vs-chip>
              </div>

              <div class="p-3 flex-grow">
                <vs-input :placeholder="$t('ImageTitleAr')" v-model="image.ImageTitleAr" class="w-full mb-2" size="small" />
                <vs-input :placeholder="$t('ImageTitleEn')" v-model="image.ImageTitleEn" class="w-full mb-2" size="small" />

                <div class="flex items-center justify-between mt-2">
                  <div class="flex items-center">
                    <small class="mr-2">{{ $t('Order') }}:</small>
                    <vs-input-number v-model="image.DisplayOrder" min="0" size="small" class="m-0" />
                  </div>
                  <vs-switch v-model="image.IsActive" size="small">
                    <span slot="on">{{ $t('Active') }}</span>
                    <span slot="off">{{ $t('Inactive') }}</span>
                  </vs-switch>
                </div>
              </div>

              <div class="p-2 border-t border-solid border-grey-light bg-grey-lighter flex justify-between gap-2">
                <vs-button
                  color="warning"
                  type="flat"
                  size="small"
                  icon-pack="feather"
                  icon="icon-star"
                  :disabled="image.IsPrimary"
                  @click="setAsPrimary(image)"
                >
                  {{ image.IsPrimary ? $t('Primary') : $t('SetAsPrimary') }}
                </vs-button>

                <vs-button
                  color="danger"
                  type="flat"
                  size="small"
                  icon-pack="feather"
                  icon="icon-trash-2"
                  @click="confirmDelete(image)"
                >
                  {{ $t('Delete') }}
                </vs-button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </vx-card>

    <vx-card v-else class="text-center p-10 mt-6">
      <feather-icon icon="CameraOffIcon" svgClasses="h-16 w-16 text-grey mb-4" />
      <h3 class="text-grey">{{ $t('NoImagesUploadedYet') }}</h3>
      <p class="text-grey mt-2">{{ $t('UseSectionAboveToUpload') }}</p>
    </vx-card>

  </div>
</template>
<script>
import moduleProperties from "@/store/Properties/moduleProperties.js";
import { domain } from "@/gloabelConstant.js";

export default {
  props: {
    property: {
       type: Object,
        required: true,
       }
  },
  data() {
    return {
      baseURL: domain,
      isDragging: false,
      isProcessing: false
    };
  },
  computed: {
    propertyImages() {

      return this.$store.state.PropertiesList ? this.$store.state.PropertiesList.propertyImages : [];
    }
  },

  methods: {


    loadImages() {
      if (this.property.Id) {
        this.$store.dispatch('PropertiesList/fetchPropertyImages', this.property.Id);
      }
    },
    onFileDrop(e) {
      this.isDragging = false;
      this.processFiles(e.dataTransfer.files);
    },
    onFileSelect(e) {
      this.processFiles(e.target.files);
      e.target.value = '';
    },
async processFiles(files) {
  if (!files || !files.length) return;
  const validFiles = Array.from(files).filter(file => file.size <= 5 * 1024 * 1024);
  if (!validFiles.length) return;

  this.isProcessing = true;
  try {
    for (const file of validFiles) {
      const uploadRes = await this.$store.dispatch(
        'PropertiesList/uploadImage',
        file
      );
      const imagePath =
        (uploadRes && uploadRes.data && uploadRes.data.path) ||
        (uploadRes && uploadRes.data && uploadRes.data.url) ||
        uploadRes.data;
      await this.$store.dispatch(
        'PropertiesList/addPropertyImage',
        {
          Id: 0,
          PropertyId: this.property.Id,
          ImagePath: imagePath,
          ImageTitleAr: "",
          ImageTitleEn: "",
          DisplayOrder: 0,
          IsPrimary: false,
          IsActive: true
        }
      );
    }
    this.loadImages();

  } catch (err) {
    console.error(err);

    this.$vs.notify({
      color: 'danger',
      title: 'Error',
      text: 'Upload failed'
    });

  } finally {
    this.isProcessing = false;
  }
},
    async confirmDelete(image) {

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('ConfirmDelete'),
        text: this.$t('AreYouSureDeleteImage'),
        accept: async () => {

          await this.$store.dispatch('PropertiesList/deletePropertyImage',image.Id);
          this.loadImages();
        }
      });
    },

    async setAsPrimary(image) {

      await this.$store.dispatch(
        'PropertiesList/updatePropertyImage',
        {
          ...image,
          IsPrimary: true
        }
      );

      this.loadImages();
    },
    async submitImages() {

  this.$vs.loading();

  try {

    const images = this.propertyImages;

    const promises = images.map(img => {

      const payload = {
        Id: img.Id,
        PropertyId: img.PropertyId,
        ImagePath: img.ImagePath,
        ImageTitleAr: img.ImageTitleAr,
        ImageTitleEn: img.ImageTitleEn,
        DisplayOrder: img.DisplayOrder,
        IsPrimary: img.IsPrimary,
        IsActive: img.IsActive
      };

      return this.$store.dispatch(
        'PropertiesList/updatePropertyImage',
        payload
      );
    });

    await Promise.all(promises);

    this.$vs.loading.close();
    this.$vs.notify({ color: 'success', title: this.$t('Success'), text: this.$t('ChangesSavedSuccessfully') });
    this.loadImages();
  } catch (error) {
    this.$vs.loading.close();
    const msg = (error.response && error.response.data && error.response.data.message)
     ? error.response.data.message : "Error saving changes";
    this.$vs.notify({ color: 'danger', title: 'Error', text: msg });
  }
}
  },
  created() {

    if (!this.$store.hasModule("PropertiesList")) {
      this.$store.registerModule("PropertiesList", moduleProperties);
    }


    this.loadImages();
  }
};
</script>

<style lang="scss" scoped>
.bg-primary-light {
  background-color: rgba(var(--vs-primary), 0.05);
}

.upload-zone {
  transition: all 0.3s ease;
  &:hover {
    border-color: rgba(var(--vs-primary), 1);
    background-color: rgba(var(--vs-primary), 0.02);
  }
}
.card-disabled {
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  filter: grayscale(0.5);
}

.cursor-not-allowed {
  cursor: not-allowed !important;
}

.img-card {
  transition: transform 0.2s;
&:hover {
  transform: translateY(-5px);
}
}

.bg-grey-lighter {
  background-color: #f8f8f8;
}

.aspect-square {
  aspect-ratio: 1 / 1;
}
</style>
